import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import copy from "copy-to-clipboard";
import MessageToster from "../component/MessageToster";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import { QRCodeCanvas } from "qrcode.react";

export default function CryptoDeposit() {
  const navigate = useNavigate();
  const handleError = useHandleError();
  const [searchParams] = useSearchParams();

  const COIN_TYPE = searchParams.get("coin") || "";
  const walletAddressParam = searchParams.get("address") || "";
  const orderNo = searchParams.get("orderno") || "";
  const invoiceid = searchParams.get("invoiceid") || "";

  const [walletAddress, setWalletAddress] = useState(walletAddressParam);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  const pollingRef = useRef(null);
  const hasCalledExpire = useRef(false);
  const isFinishedRef = useRef(false);

  // ── Cleanup on unmount ───────────────────────────────────────
  useEffect(() => {
    return () => {
      stopPolling();
      if (orderNo && !isFinishedRef.current) {
        (async () => {
          try {
            await axios.post(
              `${BASE_URL}/cansal_ststus`,
              { orderno: orderNo },
              { headers: getApiHeaders() }
            );
            const statusRes = await axios.post(
              `${BASE_URL}/fach_deposit_status`,
              { orderno: orderNo },
              { headers: getApiHeaders() }
            );
            const statusData = statusRes.data?.data || statusRes.data;
            const finalPayload = {
              orderno: orderNo,
              statusid: statusData.statusid || 0,
              status: statusData.status || "",
              coinname: statusData.coinname || "USDT",
              networkname:
                statusData.networkname || COIN_TYPE.split(" ")[0],
              tranhash: statusData.tranhash || "",
              address: statusData.address || walletAddress,
              amount: String(
                statusData.amount || searchParams.get("amount") || ""
              ),
            };
            await axios.post(`${BASE_URL}/deposit`, finalPayload, {
              headers: getApiHeaders(),
            });
          } catch (err) {
            console.error("Unmount cancel flow failed:", err);
          }
        })();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  // ── Helpers ──────────────────────────────────────────────────
  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "";
    if (seconds <= 0) return "Expired";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const copytext = (text) => {
    copy(text);
    setCopied(true);
    MessageToster("success", "Wallet address copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Final status handler ─────────────────────────────────────
  const handleFinalStatus = async (statusData, order) => {
    isFinishedRef.current = true;
    stopPolling();
    const finalPayload = {
      orderno: order,
      statusid: statusData.statusid || 0,
      status: statusData.status || "",
      coinname: statusData.coinname || "USDT",
      networkname: statusData.networkname || COIN_TYPE.split(" ")[0],
      tranhash: statusData.tranhash || "",
      address: statusData.address || walletAddress,
      amount: String(
        statusData.amount || searchParams.get("amount") || ""
      ),
    };

    try {
      const res = await axios.post(`${BASE_URL}/deposit`, finalPayload, {
        headers: getApiHeaders(),
      });
      if (res.data && res.data.status === false) {
        MessageToster("error", res.data.message || "Deposit request failed");
        return;
      }
      if (statusData.statusid === 4 || statusData.status === "success") {
        navigate(`/payment-success`, {
          replace: true,
          state: { paymentData: { ...statusData, invoiceid } },
        });
      } else {
        navigate(`/payment-failed`, {
          replace: true,
          state: { paymentData: { ...statusData, invoiceid } },
        });
      }
    } catch (err) {
      console.error("Error calling deposit API:", err);
      MessageToster(
        "error",
        err.response?.data?.message || err.message || "Error calling deposit API"
      );
    }
  };

  // ── Expire handler ───────────────────────────────────────────
  const handleExpireDeposit = async () => {
    if (!orderNo) return;
    setLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/expiry_status`,
        { orderno: orderNo },
        { headers: getApiHeaders() }
      );
      const statusRes = await axios.post(
        `${BASE_URL}/fach_deposit_status`,
        { orderno: orderNo },
        { headers: getApiHeaders() }
      );
      const statusData = statusRes.data?.data || statusRes.data;
      await handleFinalStatus(statusData, orderNo);
    } catch (error) {
      console.error("Failed in expiry sequence:", error);
      navigate(`/payment-failed`, {
        replace: true,
        state: { paymentData: { orderno: orderNo, invoiceid, status: "expire" } },
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel handler ───────────────────────────────────────────
  const handleCancelDeposit = async () => {
    if (!orderNo) return;
    setLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/cansal_ststus`,
        { orderno: orderNo },
        { headers: getApiHeaders() }
      );
      const statusRes = await axios.post(
        `${BASE_URL}/fach_deposit_status`,
        { orderno: orderNo },
        { headers: getApiHeaders() }
      );
      const statusData = statusRes.data?.data || statusRes.data;
      await handleFinalStatus(statusData, orderNo);
    } catch (error) {
      console.error("Failed in cancel sequence:", error);
      navigate(`/payment-failed`, {
        replace: true,
        state: { paymentData: { orderno: orderNo, invoiceid, status: "cancel" } },
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Countdown timer ─────────────────────────────────────────
  useEffect(() => {
    if (timeLeft === null || timeLeft === 0) {
      if (timeLeft === 0 && !hasCalledExpire.current) {
        hasCalledExpire.current = true;
        handleExpireDeposit();
      }
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!hasCalledExpire.current) {
            hasCalledExpire.current = true;
            handleExpireDeposit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft === null]);

  // ── Status polling ───────────────────────────────────────────
  const startStatusPolling = (order) => {
    stopPolling();
    const checkStatus = async () => {
      try {
        const response = await axios.post(
          `${BASE_URL}/fach_deposit_status`,
          { orderno: order },
          { headers: getApiHeaders() }
        );
        const resData = response.data?.data || response.data;
        if (resData) {
          if (resData.expirydate && timeLeft === null) {
            const dateString = resData.expirydate.endsWith("Z")
              ? resData.expirydate
              : `${resData.expirydate}Z`;
            const diffInSeconds = Math.floor(
              (new Date(dateString).getTime() - Date.now()) / 1000
            );
            setTimeLeft(diffInSeconds > 0 ? diffInSeconds : 0);
          }
          if (!resData.expirydate && timeLeft === null) {
            setTimeLeft(900); // 15 min default
          }
          if (
            resData.status === "success" ||
            resData.statusid === 4 ||
            resData.status === "failed" ||
            resData.status === "Reject" ||
            resData.code === 400 ||
            resData.statusid === 7
          ) {
            await handleFinalStatus(resData, order);
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    };
    checkStatus();
    pollingRef.current = setInterval(checkStatus, 15000);
  };

  useEffect(() => {
    if (orderNo) {
      startStatusPolling(orderNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* Page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">Deposit</h2>
                  </div>
                </div>
              </div>

              <div className="row justify-content-center">
                <div className="row mt-4">
                  <div className="col-lg-12">
                    <div
                      className="card shadow-sm border-0"
                      style={{ borderRadius: "15px" }}
                    >
                      <div className="card-body p-4">
                        <div className="row g-4">

                          {/* ── Left: QR & Address ─── */}
                          <div className="col-md-5 text-center border-end">
                            <div className="mb-4">
                              <h5
                                className="text-muted fw-bold text-uppercase mb-3"
                                style={{ letterSpacing: "1px" }}
                              >
                                Scan QR Code
                              </h5>
                              <div className="d-inline-block p-3 bg-light rounded-4 shadow-sm mb-3">
                                {!walletAddress && (
                                  <div
                                    className="d-flex flex-column align-items-center justify-content-center text-danger"
                                    style={{
                                      width: 220,
                                      height: 220,
                                      border: "2px dashed #ec822b",
                                      borderRadius: "12px",
                                      background: "#fffaf5",
                                    }}
                                  >
                                    <i
                                      className="bx bx-error-circle mb-2"
                                      style={{ fontSize: "28px" }}
                                    ></i>
                                    <p className="mb-0 small fw-semibold text-center">
                                      Wallet address not available
                                    </p>
                                    <p className="mb-0 small text-center">
                                      Please try again later
                                    </p>
                                  </div>
                                )}
                                {walletAddress && (
                                  <QRCodeCanvas
                                    value={walletAddress}
                                    size={220}
                                    marginSize={1}
                                  />
                                )}
                              </div>
                            </div>

                            <div className="px-3">
                              <label
                                className="form-label text-muted fw-semibold"
                                style={{ fontSize: "1.0rem" }}
                              >
                                Wallet Address
                              </label>
                              <div className="input-group mb-2 shadow-sm rounded-3 overflow-hidden">
                                <input
                                  type="text"
                                  className={`form-control border-0 py-2 px-3 font-monospace ${!walletAddress
                                    ? "bg-light text-muted"
                                    : "bg-white text-dark"
                                    }`}
                                  value={walletAddress || ""}
                                  placeholder={
                                    !walletAddress
                                      ? "Wallet address not available"
                                      : ""
                                  }
                                  readOnly
                                  style={{ fontSize: "0.9rem" }}
                                />
                                <button
                                  className={`btn px-3 ${copied ? "btn-success" : "btn-primary"
                                    }`}
                                  style={{ transition: "all 0.3s ease" }}
                                  onClick={() =>
                                    walletAddress && copytext(walletAddress)
                                  }
                                  disabled={!walletAddress}
                                  title={
                                    !walletAddress
                                      ? "No address to copy"
                                      : "Copy address"
                                  }
                                >
                                  <i
                                    className={`bx ${copied ? "bx-check" : "bx-copy"
                                      }`}
                                  ></i>
                                </button>
                              </div>
                              <p className="text-danger small mt-2">
                                <i className="bx bx-info-circle me-1"></i>
                                Only send <strong className="notranslate" translate="no">USDT</strong> to this address.
                              </p>

                              {/* Timer & Cancel */}
                              <div className="mt-4 text-center">
                                {timeLeft !== null && (
                                  <div className="fs-3 fw-bold text-danger mb-3 d-flex align-items-center justify-content-center notranslate" translate="no">
                                    <i className="bx bx-time-five me-2"></i>
                                    {formatTime(timeLeft)}
                                  </div>
                                )}
                                <button
                                  className="btn btn-outline-danger px-4 rounded-pill fw-medium shadow-sm"
                                  onClick={handleCancelDeposit}
                                  disabled={
                                    loading ||
                                    (timeLeft !== null && timeLeft <= 0)
                                  }
                                  style={{ transition: "all 0.3s ease" }}
                                >
                                  {loading ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                      Cancelling...
                                    </>
                                  ) : (
                                    <>
                                      <i className="bx bx-x me-1"></i> Cancel
                                      Deposit
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* ── Right: Details ─── */}
                          <div className="col-md-7 ps-md-5">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <div className="d-flex align-items-center gap-2">
                                <h4 className="mb-0 fw-bold">Deposit <span className="notranslate" translate="no">USDT</span></h4>
                                <span
                                  className="badge rounded-pill bg-success-subtle text-success border border-success-subtle notranslate"
                                  translate="no"
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  {COIN_TYPE.split(" ")[0]} Network
                                </span>
                              </div>
                            </div>

                            <div className="p-4 rounded-4 border">
                              <h6 className="fw-bold mb-3">
                                Transaction Details
                              </h6>
                              <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">Commission</span>
                                <span className="fw-bold text-success notranslate" translate="no">
                                  0.00%
                                </span>
                              </div>
                              <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">
                                  Processing Time
                                </span>
                                <span className="fw-bold">
                                  ~ 1 Hour
                                </span>
                              </div>
                              <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">
                                  Minimum Deposit
                                </span>
                                <span className="fw-bold notranslate" translate="no">
                                  10 USD
                                </span>
                              </div>
                              <div className="d-flex justify-content-between py-2">
                                <span className="text-muted">Maximum Limit</span>
                                <span className="fw-bold notranslate" translate="no">
                                  100,000 USD
                                </span>
                              </div>
                            </div>

                            <div
                              className="mt-4 p-3 border border-warning border-dashed rounded-3"
                              style={{
                                backgroundColor: "rgba(236, 198, 122, 0.1)",
                              }}
                            >
                              <small className="text-warning">
                                <strong>Notice:</strong>{" "}
                                Deposits are credited after 12 network
                                confirmations. Please ensure the network matches
                                or funds may be lost.
                              </small>
                            </div>
                          </div>
                          {/* ── End Right ─── */}

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* container-fluid */}
          </div>
          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}
